/**
 * TRACE API client
 * Wraps all backend calls so the rest of the frontend never imports fetch directly.
 * Base URL is read from REACT_APP_API_URL (set in .env).
 */

const BASE = process.env.NODE_ENV === 'production'
  ? '/api/v1'
  : (process.env.REACT_APP_API_URL ?? 'http://localhost:8000/api/v1');

// ── Helpers ───────────────────────────────────────────────────────

function getToken(): string {
  return localStorage.getItem('trace_token') ?? '';
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeaders(),
    ...extraHeaders,
  };
  if (body === undefined) delete headers['Content-Type'];

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail ?? `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ── Auth ──────────────────────────────────────────────────────────

export interface UserInfo {
  email: string;
  name: string;
  initials: string;
}

export interface LoginResponse {
  token: string;
  user: UserInfo;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const data = await api.post<LoginResponse>('/auth/login', { email, password });
  localStorage.setItem('trace_token', data.token);
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout').catch(() => { });
  localStorage.removeItem('trace_token');
}

export async function getMe(): Promise<UserInfo> {
  return api.get<UserInfo>('/auth/me');
}

// ── Documents ─────────────────────────────────────────────────────

export interface AnalysisDocument {
  id: string;
  docName: string;
  fileName: string;
  fileType: string;
  fileSize: string;
  templateVersion: string;
  templateFileName?: string;
  uploadDate: string;
  status: 'processing' | 'completed' | 'failed';
  qualityRating?: 'satisfactory' | 'needs_improvement' | 'unsatisfactory';
  issuesDetected?: number;
  findings?: Finding[];
  processingProgress?: number;
  analysisOverview?: string;
}

export interface Finding {
  id: string;
  category: string;
  severity: string;
  section: string;
  title: string;
  description: string;
  aiComment: string;
  recommendation: string;
}

export async function uploadDocument(
  file: File,
  docName: string,
  templateVersion: string,
  templateFile?: File,
): Promise<AnalysisDocument> {
  const form = new FormData();
  form.append('file', file);
  form.append('doc_name', docName);
  form.append('template_version', templateVersion);
  if (templateFile) {
    form.append('template_file', templateFile);
  }

  const res = await fetch(`${BASE}/documents/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail ?? `Upload failed: HTTP ${res.status}`);
  }
  return res.json();
}

export async function listDocuments(): Promise<AnalysisDocument[]> {
  return api.get<AnalysisDocument[]>('/documents');
}

export async function getDocument(id: string): Promise<AnalysisDocument> {
  return api.get<AnalysisDocument>(`/documents/${id}`);
}

export async function getDocumentStatus(id: string): Promise<{
  id: string;
  status: string;
  processingProgress: number;
  qualityRating?: string;
  issuesDetected?: number;
}> {
  return api.get(`/documents/${id}/status`);
}

export async function deleteDocument(id: string): Promise<void> {
  return api.delete(`/documents/${id}`);
}

// ── Dashboard ─────────────────────────────────────────────────────

export async function getDashboardStats(): Promise<{
  metrics: { label: string; value: string; change: number; trend: string }[];
  quality_distribution: { name: string; value: number; color: string }[];
  issues_by_category: { category: string; count: number; percentage: number }[];
}> {
  return api.get('/dashboard/stats');
}

// ── Feedback ──────────────────────────────────────────────────────

export interface FeedbackItem {
  id: string;
  documentId: string;
  type: string;
  severity: string;
  title: string;
  description: string;
  status: 'open' | 'in_review' | 'resolved';
  assignee: string;
  createdAt: string;
  proposedAction: string;
}

export async function listFeedback(): Promise<FeedbackItem[]> {
  return api.get<FeedbackItem[]>('/feedback');
}

export async function createFeedback(data: {
  document_id: string;
  type: string;
  severity: string;
  title: string;
  description: string;
  assignee?: string;
  proposed_action?: string;
}): Promise<FeedbackItem> {
  return api.post<FeedbackItem>('/feedback', data);
}

export async function updateFeedbackStatus(
  id: string,
  status: 'open' | 'in_review' | 'resolved',
): Promise<FeedbackItem> {
  return api.patch<FeedbackItem>(`/feedback/${id}/status`, { status });
}

export async function applyFeedbackAction(id: string): Promise<FeedbackItem> {
  return api.post<FeedbackItem>(`/feedback/${id}/apply`);
}

export async function dismissFeedback(id: string): Promise<void> {
  return api.delete(`/feedback/${id}`);
}

// ── Notifications ─────────────────────────────────────────────────

export interface Notification {
  id: string;
  type: 'critical' | 'warning' | 'success' | 'info';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  documentId?: string;
}

export async function listNotifications(): Promise<Notification[]> {
  return api.get<Notification[]>('/notifications');
}

export async function markNotificationRead(id: string): Promise<void> {
  await api.patch(`/notifications/${id}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await api.post('/notifications/read-all');
}

// ── Health ────────────────────────────────────────────────────────

export async function checkHealth(): Promise<{ status: string; version: string }> {
  const res = await fetch(`${BASE.replace('/api/v1', '')}/health`);
  return res.json();
}
